import React, { useMemo } from "react";
import { Col, Form, InputNumber, Row, Select, Space } from "antd";
import {
  Explanation,
  Game,
  GameWithTags,
  QuizSituation,
} from "../../../services/gameQuiz/gameQuiz.types";
import { GameTagDefinition } from "../../../services/gameTags/gameTags.types";
import MultipleChoiceEditor from "./games/MultipleChoiceEditor";
import TrueFalseEditor from "./games/TrueFalseEditor";
import DragAndDropEditor from "./games/DragAndDropEditor";
import DragAndDropHorizontalEditor from "./games/DragAndDropHorizontalEditor";
import FillInBlanksEditor from "./games/FillInBlanksEditor";
import MatchingEditor from "./games/MatchingEditor";
import CategorizationEditor from "./games/CategorizationEditor";
import TextInputEditor from "./games/TextInputEditor";
import TextInputAiEditor from "./games/TextInputAiEditor";
import ImageLabelingEditor from "./games/ImageLabelingEditor";
import ImageHotspotEditor from "./games/ImageHotspotEditor";
import TextSelectionEditor from "./games/TextSelectionEditor";
import ExplanationEditor from "./ExplanationEditor";
import ExamCourseSelect from "./ExamCourseSelect";
import { situationLabel } from "./examSituations";

const NO_SITUATION = "";

type Props = {
  game: GameWithTags;
  onChange: (game: GameWithTags) => void;
  generateAudio?: (text: string) => Promise<string>;
  generateAudioWithBlanks?: (text: string) => Promise<string>;
  bloomsTags: GameTagDefinition[];
  pisaTags: GameTagDefinition[];
  isExam?: boolean;
  situations?: QuizSituation[];
};

const renderTypeEditor = (
  game: Game,
  onChange: (game: Game) => void,
  generateAudio?: (text: string) => Promise<string>,
  generateAudioWithBlanks?: (text: string) => Promise<string>,
) => {
  switch (game.type) {
    case "multiple_choice":
      return (
        <MultipleChoiceEditor
          game={game}
          onChange={onChange}
          generateAudio={generateAudio}
        />
      );
    case "true_false":
      return (
        <TrueFalseEditor
          game={game}
          onChange={onChange}
          generateAudio={generateAudio}
        />
      );
    case "drag_and_drop":
      return (
        <DragAndDropEditor
          game={game}
          onChange={onChange}
          generateAudio={generateAudio}
        />
      );
    case "drag_and_drop_horizontal":
      return (
        <DragAndDropHorizontalEditor
          game={game}
          onChange={onChange}
          generateAudio={generateAudio}
        />
      );
    case "fill_in_blanks":
      return (
        <FillInBlanksEditor
          game={game}
          onChange={onChange}
          generateAudio={generateAudio}
          generateAudioWithBlanks={generateAudioWithBlanks}
        />
      );
    case "matching":
      return (
        <MatchingEditor
          game={game}
          onChange={onChange}
          generateAudio={generateAudio}
        />
      );
    case "categorization":
      return (
        <CategorizationEditor
          game={game}
          onChange={onChange}
          generateAudio={generateAudio}
        />
      );
    case "text_input":
      return (
        <TextInputEditor
          game={game}
          onChange={onChange}
          generateAudio={generateAudio}
        />
      );
    case "text_input_ai":
      return (
        <TextInputAiEditor
          game={game}
          onChange={onChange}
          generateAudio={generateAudio}
        />
      );
    case "image_labeling":
      return (
        <ImageLabelingEditor
          game={game}
          onChange={onChange}
          generateAudio={generateAudio}
        />
      );
    case "image_hotspot":
      return (
        <ImageHotspotEditor
          game={game}
          onChange={onChange}
          generateAudio={generateAudio}
        />
      );
    case "text_selection":
      return (
        <TextSelectionEditor
          game={game}
          onChange={onChange}
          generateAudio={generateAudio}
        />
      );
    default:
      return null;
  }
};

const GameItemEditor: React.FC<Props> = ({
  game,
  onChange,
  generateAudio,
  generateAudioWithBlanks,
  bloomsTags,
  pisaTags,
  isExam = false,
  situations = [],
}) => {
  const bloomsIds = useMemo(
    () => new Set(bloomsTags.map((t) => t.id)),
    [bloomsTags],
  );
  const pisaIds = useMemo(
    () => new Set(pisaTags.map((t) => t.id)),
    [pisaTags],
  );

  const tagIds = game.tagIds ?? [];
  const selectedBlooms = tagIds.filter((id) => bloomsIds.has(id));
  const selectedPisa = tagIds.filter((id) => pisaIds.has(id));

  const handleBloomsChange = (next: number[]) => {
    const preservedPisa = tagIds.filter((id) => pisaIds.has(id));
    onChange({ ...game, tagIds: [...next, ...preservedPisa] });
  };

  const handlePisaChange = (next: number[]) => {
    const preservedBlooms = tagIds.filter((id) => bloomsIds.has(id));
    onChange({ ...game, tagIds: [...preservedBlooms, ...next] });
  };

  const handleTypeEditorChange = (updated: Game) => {
    onChange({ ...updated, tagIds: game.tagIds, tags: game.tags });
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Form layout="vertical" component="div">
        {isExam && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Puntaje"
                required
                style={{ marginBottom: 0 }}
                validateStatus={(game.points ?? 0) > 0 ? undefined : "error"}
                help={
                  (game.points ?? 0) > 0 ? undefined : "Debe ser mayor que 0"
                }
              >
                <InputNumber
                  aria-label="Puntaje"
                  min={0}
                  step={0.5}
                  style={{ width: "100%" }}
                  value={game.points ?? null}
                  onChange={(points) =>
                    onChange({ ...game, points: points ?? undefined })
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={18}>
              <Form.Item
                label="Curso que evalúa"
                required
                style={{ marginBottom: 0 }}
              >
                <ExamCourseSelect
                  value={game.course_id}
                  onChange={(courseId) =>
                    onChange({ ...game, course_id: courseId })
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} style={{ marginTop: 16 }}>
              <Form.Item label="Situación" style={{ marginBottom: 0 }}>
                <Select
                  aria-label="Situación"
                  value={game.situation_id ?? NO_SITUATION}
                  onChange={(situationId: string) => {
                    const { situation_id: _previous, ...rest } = game;
                    onChange(
                      situationId === NO_SITUATION
                        ? (rest as GameWithTags)
                        : { ...game, situation_id: situationId },
                    );
                  }}
                  options={[
                    { value: NO_SITUATION, label: "Sin situación" },
                    ...situations.map((situation) => ({
                      value: situation.id,
                      label: situationLabel(situations, situation.id),
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Tags Blooms" style={{ marginBottom: 0 }}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Selecciona tags Blooms"
                value={selectedBlooms}
                onChange={handleBloomsChange}
                options={bloomsTags.map((t) => ({
                  value: t.id,
                  label: t.name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Tags PISA" style={{ marginBottom: 0 }}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Selecciona tags PISA"
                value={selectedPisa}
                onChange={handlePisaChange}
                options={pisaTags.map((t) => ({ value: t.id, label: t.name }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {renderTypeEditor(
        game as Game,
        handleTypeEditorChange,
        generateAudio,
        generateAudioWithBlanks,
      )}

      <ExplanationEditor
        value={game.explanation}
        onChange={(explanation: Explanation | undefined) =>
          onChange({ ...game, explanation } as GameWithTags)
        }
      />
    </Space>
  );
};

export default GameItemEditor;
